import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Card, Form, Input, Select, DatePicker, Checkbox, ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import enUS from 'antd/locale/en_US';

interface WorkExperienceCardProps {
  intl: any;
  visible: boolean;
  workExperienceTypes?: any[];
  industries?: any[];
  jobLevels?: any[];
  dispatch?: any;
}

// Define the handle type for autocomplete/TypeScript support in parent
export interface WorkExperienceCardRef {
  validateAndGetValues: () => Promise<any>;
}

export const WorkExperienceCard = forwardRef<WorkExperienceCardRef, WorkExperienceCardProps>(({
  intl,
  visible,
  workExperienceTypes = [],
  industries = [],
  jobLevels = []
}, ref) => {
  const [form] = Form.useForm();
  const [isWorking, setIsWorking] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedJobLevel, setSelectedJobLevel] = useState('');

  // Expose validation and data formatting to the parent formik wrapper
  useImperativeHandle(ref, () => ({
    validateAndGetValues: async () => {
      try {
        const values = await form.validateFields();
        // Automatically formats DayJS date objects to clean strings before returning
        return {
          ...values,
          industryId: selectedIndustry === 'Other' ? 0 : selectedIndustry,
          jobLevelId: selectedJobLevel === 'Other' ? 0 : selectedJobLevel,
          startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
          endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        };
      } catch (error) {
        throw error; // Re-throw so parent's onSubmit catches it and stops submission
      }
    }
  }));

  if (!visible) return null;

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const rules = {
    employerType: [{ required: true, message: intl.formatMessage({ id: 'FORM_VALIDATION.REQUIRED' }) }],
    company: [{ required: true, message: intl.formatMessage({ id: 'FORM_VALIDATION.REQUIRED' }) }],
    startDate: [{ required: true, message: intl.formatMessage({ id: 'FORM_VALIDATION.REQUIRED' }) }],
    industry: [{ required: true, message: intl.formatMessage({ id: 'FORM_VALIDATION.REQUIRED' }) }]
  };

  return (
    <Card 
      title={intl.formatMessage({ id: 'WORKEXPERIENCE.ADDNEW' })} 
      className="mb-8 border border-dashed border-primary bg-light-primary bg-opacity-10"
      styles={{ body: { padding: '2rem' } }} // Updated from legacy bodyStyle
    >
      <ConfigProvider locale={intl.locale === 'tr' ? trTR : enUS}>
        {/* component={false} prevents nesting an HTML <form> inside your parent's HTML <form> */}
        <Form form={form} component={false}>
          
          <Form.Item name="employerTypeId" label={intl.formatMessage({ id: 'WORKEXPERIENCE.EMPLOYERTYPE' })} rules={rules.employerType} {...formItemLayout}>
            <Select>
              {workExperienceTypes.map((workExp: any) => (
                <Select.Option key={workExp.id} value={workExp.id}>{workExp.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='companyName' label={intl.formatMessage({ id: 'WORKEXPERIENCE.COMPANY' })} rules={rules.company} {...formItemLayout}>
            <Input />
          </Form.Item>

          <Form.Item name='startDate' label={intl.formatMessage({ id: 'WORKEXPERIENCE.STARTDATE' })} rules={rules.startDate} {...formItemLayout}>
            <DatePicker format={intl.locale === 'tr' ? 'DD.MM.YYYY' : 'YYYY-MM-DD'} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="isWorking" initialValue={true} label={intl.formatMessage({ id: 'WORKEXPERIENCE.STATE' })} valuePropName="checked" {...formItemLayout}>
            <Checkbox onChange={(e) => setIsWorking(e.target.checked)} style={{ userSelect: 'none' }}>
              {intl.formatMessage({ id: 'WORKEXPERIENCE.IAMWORKING' })}
            </Checkbox>
          </Form.Item>

          {!isWorking && (
            <Form.Item name='endDate' label={intl.formatMessage({ id: 'WORKEXPERIENCE.ENDDATE' })} rules={rules.startDate} {...formItemLayout}>
              <DatePicker format={intl.locale === 'tr' ? 'DD.MM.YYYY' : 'YYYY-MM-DD'} style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item name="industryId" label={intl.formatMessage({ id: 'WORKEXPERIENCE.INDUSTRY' })} rules={rules.industry} {...formItemLayout}>
            <Select onChange={(value) => setSelectedIndustry(value)}>
              {industries.map((industry: any) => (
                <Select.Option key={industry.id} value={industry.id}>{industry.name}</Select.Option>
              ))}
              <Select.Option value="Other">{intl.formatMessage({ id: 'WORKEXPERIENCE.OTHER' })}</Select.Option>
            </Select>
          </Form.Item>

          {selectedIndustry === 'Other' && (
            <Form.Item name="industryOther" label={intl.formatMessage({ id: 'WORKEXPERIENCE.INDUSTRY.OTHER' })} {...formItemLayout}>
              <Input />
            </Form.Item>
          )}

          <Form.Item name="jobLevelId" label={intl.formatMessage({ id: 'WORKEXPERIENCE.JOBLEVEL' })} rules={rules.industry} {...formItemLayout}>
            <Select onChange={(value) => setSelectedJobLevel(value)}>
              {jobLevels.map((jobLevel: any) => (
                <Select.Option key={jobLevel.id} value={jobLevel.id}>{jobLevel.name}</Select.Option>
              ))}
              <Select.Option value="Other">{intl.formatMessage({ id: 'WORKEXPERIENCE.OTHER' })}</Select.Option>
            </Select>
          </Form.Item>

          {selectedJobLevel === 'Other' && (
            <Form.Item name="jobLevelOther" label={intl.formatMessage({ id: 'WORKEXPERIENCE.JOBLEVEL.OTHER' })} {...formItemLayout}>
              <Input />
            </Form.Item>
          )}

          <Form.Item name="summary" label={intl.formatMessage({ id: 'WORKEXPERIENCE.SUMMARY' })} {...formItemLayout}>
            <Input.TextArea rows={3} />
          </Form.Item>

        </Form>
      </ConfigProvider>
    </Card>
  );
});

WorkExperienceCard.displayName = 'WorkExperienceCard';